import { useQuery } from '@tanstack/react-query';
import type { HealthResponse, ReadyResponse } from '@castinflow/shared';
import { Badge, Card, CardBody, DescriptionList, PageHeader, Spinner } from '@castinflow/ui';
import { apiFetch } from '../lib/api';
import { usePageMeta } from '../shell/page-meta';
import { tr } from '../i18n/tr';

/** API sağlık/hazırlık durumu. Oturum dışı da açılır (giriş sorunlarında ilk bakılacak yer). */
export function StatusPage() {
  usePageMeta(tr.status.title);
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => apiFetch<HealthResponse>('/health'),
  });
  const ready = useQuery({
    queryKey: ['ready'],
    queryFn: async () => {
      // /ready 503 döndürebilir; zarf yerine kontrol listesi gelir
      const res = await fetch('/api/ready');
      return (await res.json()) as ReadyResponse;
    },
  });

  const checking = (
    <span className="inline-flex items-center gap-2 text-fg-muted">
      <Spinner size="sm" decorative />
      {tr.status.checking}
    </span>
  );

  return (
    <section data-testid="status-page">
      <PageHeader title={tr.status.title} description={tr.status.description} />
      <Card>
        <CardBody>
          <DescriptionList
            items={[
              {
                term: tr.status.api,
                value: (
                  <span data-testid="health">
                    {health.isPending
                      ? checking
                      : health.isError
                        ? tr.status.unreachable
                        : tr.status.running(health.data.version)}
                  </span>
                ),
              },
              {
                term: tr.status.readiness,
                value: (
                  <span data-testid="ready">
                    {ready.isPending
                      ? checking
                      : ready.isError
                        ? tr.status.unreachable
                        : ready.data.status === 'ready'
                          ? tr.status.ready
                          : tr.status.notReady}
                  </span>
                ),
              },
            ]}
          />
        </CardBody>
        {ready.data ? (
          <div className="overflow-x-auto border-t border-border">
            <table className="w-full text-sm">
              <caption className="px-4 pb-2 pt-3 text-left text-base font-medium text-fg sm:px-5">
                {tr.status.checks}
              </caption>
              <thead className="bg-surface-subtle">
                <tr className="border-y border-border text-left text-fg-muted">
                  <th scope="col" className="h-row px-4 font-medium sm:px-5">
                    {tr.status.colService}
                  </th>
                  <th scope="col" className="h-row px-4 font-medium sm:px-5">
                    {tr.status.colResult}
                  </th>
                  <th scope="col" className="h-row px-4 text-right font-medium sm:px-5">
                    {tr.status.colDuration}
                  </th>
                </tr>
              </thead>
              <tbody>
                {ready.data.checks.map((c) => (
                  <tr key={c.name} className="border-b border-border last:border-b-0">
                    <th scope="row" className="h-row px-4 text-left font-normal text-fg sm:px-5">
                      {tr.status.serviceNames[c.name] === undefined ? (
                        <code className="font-mono">{c.name}</code>
                      ) : (
                        <span className="inline-flex flex-col">
                          <span>{tr.status.serviceNames[c.name]}</span>
                          <code className="font-mono text-sm text-fg-muted">{c.name}</code>
                        </span>
                      )}
                    </th>
                    <td className="h-row px-4 sm:px-5">
                      {c.ok ? (
                        <Badge tone="success" toneIcon>
                          {tr.status.ok}
                        </Badge>
                      ) : (
                        <Badge tone="danger" toneIcon>
                          {tr.status.failed}
                        </Badge>
                      )}
                    </td>
                    <td className="h-row px-4 text-right tabular-nums text-fg-muted sm:px-5">
                      {tr.status.ms(c.durationMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </section>
  );
}
